import { logPhotoFlow } from '../diagnostics/photoFlow';
import { preparePhotoImportFile, type PhotoDecodeOptions } from '../editor/image';
import { LOCAL_PHOTOS_STORE, openLocalDatabase } from './database';

const STORE = LOCAL_PHOTOS_STORE;
export const LOCAL_PHOTO_TTL_MS = 24 * 60 * 60 * 1000;
export const LOCAL_PHOTO_TOUCH_INTERVAL_MS = 60 * 60 * 1000;

interface StoredPhotoRecord {
  id: string;
  blob: Blob;
  mime: string;
  bytes: number;
  lastModified: number;
  createdAt: number;
  lastAccessedAt?: number;
}

export interface LocalPhotoReference {
  id: string;
  mime: string;
  bytes: number;
}

export class LocalPhotoStorageError extends Error {
  constructor(public readonly code: 'persist_failed' | 'not_found' | 'read_failed', options?: ErrorOptions) {
    super(code, options);
    this.name = 'LocalPhotoStorageError';
  }
}

function photoId(): string {
  return crypto.randomUUID?.() || `photo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function safePhotoName(mime: string): string {
  if (mime === 'image/png') return 'walld-photo.png';
  if (mime === 'image/webp') return 'walld-photo.webp';
  return 'walld-photo.jpg';
}

function errorClass(cause: unknown): string {
  return cause instanceof Error ? cause.name.slice(0, 80) : typeof cause;
}

async function putPhoto(record: StoredPhotoRecord): Promise<void> {
  const database = await openLocalDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error || new Error('The local photo transaction was aborted.'));
      transaction.onerror = () => reject(transaction.error || new Error('The local photo could not be saved.'));
    });
  } finally { database.close(); }
}

async function touchLocalPhoto(id: string, accessedAt: number): Promise<void> {
  const database = await openLocalDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      const store = transaction.objectStore(STORE);
      const request = store.get(id);
      request.onsuccess = () => {
        const current = (request.result as StoredPhotoRecord | undefined) || null;
        if (!current) return;
        const previousAccess = Number(current.lastAccessedAt ?? current.createdAt);
        if (Number.isFinite(previousAccess) && accessedAt - previousAccess < LOCAL_PHOTO_TOUCH_INTERVAL_MS) return;
        store.put({ ...current, lastAccessedAt: accessedAt });
      };
      request.onerror = () => reject(request.error || new Error('The local photo metadata could not be read.'));
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error || new Error('The local photo metadata transaction was aborted.'));
      transaction.onerror = () => reject(transaction.error || new Error('The local photo metadata could not be updated.'));
    });
  } finally { database.close(); }
}

export async function prepareAndStoreLocalPhoto(
  source: File,
  options: PhotoDecodeOptions & { maxEdge?: number; quality?: number } = {},
): Promise<LocalPhotoReference> {
  const id = photoId();
  const maxEdge = options.maxEdge ?? 2400;
  logPhotoFlow('PHOTO_DECODE_START', { photoId: id, mime: source.type || 'unknown', bytes: source.size });
  let prepared: File;
  try {
    prepared = await preparePhotoImportFile(source, maxEdge, options.quality ?? .9, { ...options, photoId: id });
    logPhotoFlow('PHOTO_DECODE_SUCCESS', { photoId: id, mime: prepared.type, bytes: prepared.size });
  } catch (cause) {
    logPhotoFlow('PHOTO_DECODE_FAIL', { photoId: id, errorClass: errorClass(cause) });
    throw cause;
  }

  const storedAt = Date.now();
  const record: StoredPhotoRecord = {
    id,
    blob: prepared.slice(0, prepared.size, prepared.type),
    mime: prepared.type,
    bytes: prepared.size,
    lastModified: prepared.lastModified || Date.now(),
    createdAt: storedAt,
    lastAccessedAt: storedAt,
  };
  logPhotoFlow('PHOTO_PERSIST_START', { photoId: id, mime: record.mime, bytes: record.bytes });
  try { await putPhoto(record); }
  catch (cause) {
    logPhotoFlow('PHOTO_PERSIST_FAIL', { photoId: id, errorClass: errorClass(cause) });
    throw new LocalPhotoStorageError('persist_failed', { cause });
  }
  logPhotoFlow('PHOTO_PERSIST_SUCCESS', { photoId: id, mime: record.mime, bytes: record.bytes });
  logPhotoFlow('PHOTO_ID', { photoId: id });
  return { id, mime: record.mime, bytes: record.bytes };
}

export async function loadLocalPhoto(id: string): Promise<File> {
  let database: IDBDatabase;
  try { database = await openLocalDatabase(); }
  catch (cause) { throw new LocalPhotoStorageError('read_failed', { cause }); }
  try {
    const record = await new Promise<StoredPhotoRecord | null>((resolve, reject) => {
      const request = database.transaction(STORE, 'readonly').objectStore(STORE).get(id);
      request.onsuccess = () => resolve((request.result as StoredPhotoRecord | undefined) || null);
      request.onerror = () => reject(request.error || new Error('The local photo could not be loaded.'));
    });
    if (!record || !(record.blob instanceof Blob)) throw new LocalPhotoStorageError('not_found');
    const restored = new File([record.blob], safePhotoName(record.mime), { type: record.mime, lastModified: record.lastModified });
    const accessedAt = Date.now();
    const previousAccess = Number(record.lastAccessedAt ?? record.createdAt);
    if (!Number.isFinite(previousAccess) || accessedAt - previousAccess >= LOCAL_PHOTO_TOUCH_INTERVAL_MS) {
      void touchLocalPhoto(id, accessedAt).catch(() => undefined);
    }
    return restored;
  } catch (cause) {
    if (cause instanceof LocalPhotoStorageError) throw cause;
    throw new LocalPhotoStorageError('read_failed', { cause });
  } finally { database.close(); }
}

export async function deleteLocalPhoto(id: string): Promise<void> {
  if (!id) return;
  const database = await openLocalDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).delete(id);
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error || new Error('The local photo transaction was aborted.'));
      transaction.onerror = () => reject(transaction.error || new Error('The local photo could not be deleted.'));
    });
  } finally { database.close(); }
}

export async function clearLocalPhotos(): Promise<void> {
  const database = await openLocalDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).clear();
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error || new Error('The local photo transaction was aborted.'));
      transaction.onerror = () => reject(transaction.error || new Error('The local photos could not be cleared.'));
    });
  } finally { database.close(); }
}

export async function pruneLocalPhotos(options: {
  preserveIds?: readonly string[];
  now?: number;
  ttlMs?: number;
} = {}): Promise<number> {
  const database = await openLocalDatabase();
  try {
    const records = await new Promise<StoredPhotoRecord[]>((resolve, reject) => {
      const request = database.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      request.onsuccess = () => resolve((request.result as StoredPhotoRecord[] | undefined) || []);
      request.onerror = () => reject(request.error || new Error('The local photos could not be inspected.'));
    });
    const preserveIds = new Set((options.preserveIds || []).filter(Boolean));
    const now = options.now ?? Date.now();
    const ttlMs = Math.max(0, options.ttlMs ?? LOCAL_PHOTO_TTL_MS);
    const expiredIds = records.filter((record) => {
      if (!record?.id || preserveIds.has(record.id)) return false;
      const lastActive = Number(record.lastAccessedAt ?? record.createdAt);
      return !Number.isFinite(lastActive) || now - lastActive >= ttlMs;
    }).map((record) => record.id);
    if (!expiredIds.length) return 0;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      const store = transaction.objectStore(STORE);
      expiredIds.forEach((id) => store.delete(id));
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error || new Error('The local photo transaction was aborted.'));
      transaction.onerror = () => reject(transaction.error || new Error('The local photos could not be pruned.'));
    });
    return expiredIds.length;
  } finally { database.close(); }
}
